<template>
  <div class="requestOverview">
    <div v-if="loadRequests || existingApplications.length > 0" class="headline-container">
      <h1>Deriproject</h1>
      <div class="filter-container">
        <v-text-field autofocus solo clearable label="Filter Applications:" id="filterValue" hide-details v-model="filterValue"
          ref="searchBox" />
      </div>
      <div class="clearfix"></div>
    </div>
    <Loading class="mt-12" v-if="loadRequests" :loading="loadRequests"></Loading>

    <v-container fluid v-if="filteredApplicationsWithUnreleasedChanges.length">
      <h3>Projects with Unreleased Changes:</h3>
      <v-row>
        <template v-for="application in visibleApplicationsWithUnreleasedChanges">
          <v-col cols="3" :key="`unreleased-application-${application.applicationName}`">
            <SimpleApplicationView :application="application.applicationName" :changeCount="application.changeCount"
              :contributors="application.contributors" :changes="application.changes" :ignored="false"
              @ignore="ignoreUnreleasedChanges(application)" />
          </v-col>
        </template>
      </v-row>
      <v-expansion-panels v-if="ignoredApplicationsWithUnreleasedChanges.length" flat accordion>
        <v-expansion-panel class="ignored-panel">
          <v-expansion-panel-header class="ignored-toggle">
            Show {{ ignoredApplicationsWithUnreleasedChanges.length }} ignored projects
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col cols="3" v-for="application in ignoredApplicationsWithUnreleasedChanges"
                :key="`ignored-unreleased-application-${application.applicationName}`">
                <SimpleApplicationView :application="application.applicationName" :changeCount="application.changeCount"
                  :contributors="application.contributors" :changes="application.changes" :ignored="true"
                  @unignore="unignoreUnreleasedChanges(application)" />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <v-container fluid v-if="filteredApplicationsWithUpdates.length">
      <h3>Projects with Available Updates:</h3>
      <v-row>
        <template v-for="application in visibleApplicationsWithUpdates">
          <v-col cols="3" :key="`undeployed-application-${application.name}`">
            <ApplicationWithUpdate :application="application" :deploymentCache="deploymentCache" :ignored="false"
              @ignore="ignoreAvailableUpdate(application)" />
          </v-col>
        </template>
      </v-row>
      <v-expansion-panels v-if="ignoredApplicationsWithUpdates.length" flat accordion>
        <v-expansion-panel class="ignored-panel">
          <v-expansion-panel-header class="ignored-toggle">
            Show {{ ignoredApplicationsWithUpdates.length }} ignored projects
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col cols="3" v-for="application in ignoredApplicationsWithUpdates"
                :key="`ignored-undeployed-application-${application.name}`">
                <ApplicationWithUpdate :application="application" :deploymentCache="deploymentCache" :ignored="true"
                  @unignore="unignoreAvailableUpdate(application)" />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>

    <v-container fluid>
      <v-row>
        <template v-for="(application, index) in filteredApplications">
          <v-col cols="4" :key="`application-${index}`">
            <EditApplication :application="application" :deploymentCache="deploymentCache" :portRange="portRange"
              :available-hosts="availableHosts" :available-environments="availableEnvironments"
              :available-application-types="availableApplicationTypes" @applicationEdited="applicationEdited">
            </EditApplication>
          </v-col>
        </template>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Loading from '@/components/general/PacmanLoader.vue';
import ApplicationService from "@/services/ApplicationService";
import ConfigurationOptionsService from "@/services/ConfigurationOptionsService";
import EditApplication from "@/components/dashboard/EditApplication";
import SimpleApplicationView from "@/components/dashboard/SimpleApplicationView";
import ApplicationWithUpdate from "@/components/dashboard/ApplicationWithUpdate";
import InitializerConfigurationService from "@/services/ConfigurationOptionsService";

export default {
  name: 'ApplicationOverview',
  components: {
    Loading,
    EditApplication,
    SimpleApplicationView,
    ApplicationWithUpdate
  },
  data: function () {
    return {
      loadRequests: false,
      portRange: {},
      availableHosts: [],
      availableEnvironments: [],
      availableApplicationTypes: [],
      existingApplications: [],
      applicationsWithUnreleasedChanges: [],
      applicationsWithUpdates: [],
      hostTranslations: {},
      filterValue: "",

      deploymentCache: {},
      connected: false,
      websocketUrl: process.env.VUE_APP_WEBSOCKET_BASE_URL + "/ws/applications/deploymentCache/updates",
    }
  },
  computed: {
    applicationFilter() {
      return new RegExp(this.filterValue || '', 'i')
    },
    filteredApplications() {
      let filter = this.applicationFilter
      return this.existingApplications.filter(function (application) {
        return application.name.match(filter) || application.port.toString().match(filter) || application.environmentConfigurations.some(config => config.host.name.toLowerCase().match(filter))
      })
    },
    filteredApplicationNames() {
      return new Set(this.filteredApplications.map(application => application.name))
    },
    filteredApplicationsWithUnreleasedChanges() {
      return this.applicationsWithUnreleasedChanges.filter(application =>
        this.filteredApplicationNames.has(application.applicationName) || this.applicationFilter.test(application.applicationName))
    },
    filteredApplicationsWithUpdates() {
      return this.applicationsWithUpdates.filter(application => this.filteredApplicationNames.has(application.name))
    },
    visibleApplicationsWithUnreleasedChanges() {
      return this.filteredApplicationsWithUnreleasedChanges.filter(application => !application.ignored)
    },
    ignoredApplicationsWithUnreleasedChanges() {
      return this.filteredApplicationsWithUnreleasedChanges.filter(application => application.ignored)
    },
    visibleApplicationsWithUpdates() {
      return this.filteredApplicationsWithUpdates.filter(application => application.ignoredReleaseVersion !== application.releaseVersions[0])
    },
    ignoredApplicationsWithUpdates() {
      return this.filteredApplicationsWithUpdates.filter(application => application.ignoredReleaseVersion === application.releaseVersions[0])
    }
  },
  methods: {
    resolveHostTranslation: function (name) {
      return this.hostTranslations[name];
    },
    applicationEdited: function () {
      this.existingApplications = []
      this.applicationsWithUnreleasedChanges = []
      this.loadRequests = true
      this.reloadApplications()
    },
    reloadApplications: function () {
      ApplicationService.getApplications().then(response => {
        this.existingApplications = response;
        this.applicationsWithUpdates = this.existingApplications.filter(application => {
          if (application.releaseVersions !== undefined && application.environmentConfigurations !== undefined) {
            let latestVersion = application.releaseVersions[0];
            if (latestVersion !== undefined) {
              let deploymentInformation = this.evaluateDeploymentInformation(application.name, "PRODUCTION_ENVIRONMENT")
              if (deploymentInformation !== null) {
                let deployedVersion = deploymentInformation.version;
                if (deployedVersion === undefined || deployedVersion == null) {
                  return false;
                }
                return !deployedVersion.includes(latestVersion.toString());
              }
            }
          }
          return false;
        });

        this.loadRequests = false;
      });

      ApplicationService.getApplicationsWithUnreleasedChanges().then(response => {
        this.applicationsWithUnreleasedChanges = response;
      });
    },
    ignoreUnreleasedChanges(application) {
      ApplicationService.ignoreUnreleasedChanges(application.applicationName).then(() => {
        application.ignored = true;
      });
    },
    unignoreUnreleasedChanges(application) {
      ApplicationService.unignoreUnreleasedChanges(application.applicationName).then(() => {
        application.ignored = false;
      });
    },
    ignoreAvailableUpdate(application) {
      ApplicationService.ignoreAvailableUpdate(application.name).then(() => {
        this.$set(application, 'ignoredReleaseVersion', application.releaseVersions[0]);
      });
    },
    unignoreAvailableUpdate(application) {
      ApplicationService.unignoreAvailableUpdate(application.name).then(() => {
        this.$set(application, 'ignoredReleaseVersion', null);
      });
    },
    evaluateDeploymentInformation(applicationName, environmentType) {
      if (this.deploymentCache && this.deploymentCache[applicationName] && this.deploymentCache[applicationName][environmentType]) {
        return this.deploymentCache[applicationName][environmentType];
      }
      return null;
    },
    focusOnCtrlF(event) {
      if (event.ctrlKey && event.key === 'f') {
        event.preventDefault();
        this.$refs.searchBox.focus();
      }
    }
  },
  created() {
    this.loadRequests = true;

    InitializerConfigurationService
      .retrieveAvailableHosts()
      .then(availableHosts => {
        this.availableHosts = availableHosts;
      });
    InitializerConfigurationService
      .retrieveAvailableEnvironments()
      .then(availableEnvironments => {
        this.availableEnvironments = availableEnvironments;
      });
    InitializerConfigurationService
      .retrieveAvailableApplicationTypes()
      .then(availableApplicationTypes => {
        this.availableApplicationTypes = availableApplicationTypes;
      });
    InitializerConfigurationService
      .retrievePortRange()
      .then(portRange => {
        this.portRange = {
          min: portRange.min,
          max: portRange.max
        };
      });

    ConfigurationOptionsService
      .retrieveAvailableHosts()
      .then(response => {
        this.hostTranslations = response
          .reduce(function (map, obj) {
            map[obj.name] = obj.displayName;
            return map;
          }, {})
      })

    this.reloadApplications()

    this.socket = new WebSocket(this.websocketUrl)
    this.socket.onopen = () => {
      console.log("connected to " + this.websocketUrl)
      this.connected = true
      this.socket.send("deploymentCache")
    }
    this.socket.onerror = error => {
      console.log("could not connect to " + this.websocketUrl)
      console.log(error)
    }
    this.socket.onmessage = message => {
      console.log("event received", message)
      let deploymentCache = message.data
      if (deploymentCache !== "\"COMPLETED\"") {
        this.deploymentCache = JSON.parse(deploymentCache)
        this.reloadApplications()
      }
    }
    this.socket.onclose = error => {
      console.log("disconnect from " + this.websocketUrl)
      this.connected = false
      console.log(error)
    }


  },
  mounted() {
    document.addEventListener('keydown', this.focusOnCtrlF);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.focusOnCtrlF);
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
h1 {
  margin-bottom: 25px;
  font-weight: 900;
  text-align: left;
}

h3 {
  text-align: left;
}

.headline-container h1 {
  padding-left: 12px;
  display: inline-block;
  float: left;
}

.headline-container .filter-container {
  display: inline-block;
  padding-right: 12px;
  width: 450px;
  float: right;
}

.headline-container .clearfix {
  clear: both;
}

.ignored-toggle {
  display: inline-flex;
  flex: none;
  min-height: 40px;
  padding-left: 0;
  color: rgba(0, 0, 0, 0.6);
}

.ignored-panel {
  background-color: transparent !important;
}
</style>
